/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { E2Ntable, E2NtableHeader, E2NtableRow } from '@/components/Table';
import { TableBody, TableCell, TableHead, TableRow } from '@e2n/ui';
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { HTMLAttributes, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { Konflikt } from './entities/Konflikt';
import { ToDoRowModel } from './entities/Entities';
import { renderRegistry } from './renderer/renderer';
import { SortableHeader } from '../SortableHeader';
import { t } from 'i18next';
import {
  filterMitarbeiter,
  sortMitarbeiter,
} from './columnFunctions/mitarbeiter';
import {
  filterArbeitsbereich,
  sortArbeitsbereich,
} from './columnFunctions/arbeitsbereiche';
import { Abteilung, Arbeitsbereich } from '@/domain/arbeitszeiten/entities';
import { sortArbeitszeit } from './columnFunctions/arbeitszeit';
import { sortDauer } from './columnFunctions/dauer';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { NoDataFallback } from '@/domain/arbeitszeiten/shared/components/NoDataFallback';

export function CellContent({
  className,
  border = true,
  ...props
}: HTMLAttributes<HTMLDivElement> & { border?: boolean }) {
  return (
    <div
      {...props}
      className={cn(`p-2 h-16 flex flex-row items-center`, className)}>
      <div className="w-full">{props.children}</div>
      <div
        className={`self-stretch h-12   ${
          border ? 'border-solid border-r border-neutrals-300' : ''
        }`}
      />
    </div>
  );
}

const columns: ColumnDef<ToDoRowModel>[] = [
  {
    accessorKey: 'Mitarbeiter',
    header: ({ column }) => (
      <SortableHeader
        title={t('entities.name_one')}
        sorted={column.getIsSorted()}
      />
    ),
    cell: (props) => {
      const row = props.row.original;
      return (
        renderRegistry.get(row._type)?.name(props.row.original) ?? <>Nothing</>
      );
    },
    sortingFn: sortMitarbeiter,
    filterFn: filterMitarbeiter,
  },
  {
    accessorKey: 'Arbeitsbereich',
    header: ({ column }) => (
      <SortableHeader
        title={t('entities.department_one')}
        sorted={column.getIsSorted()}
      />
    ),
    cell: (props) => {
      const row = props.row.original;
      return (
        renderRegistry.get(row._type)?.arbeitsbereich(props.row.original) ?? (
          <>Nothing</>
        )
      );
    },
    sortingFn: sortArbeitsbereich,
    filterFn: filterArbeitsbereich,
  },
  {
    accessorKey: 'Arbeitszeit',
    header: ({ column }) => (
      <SortableHeader
        title={t('entities.workingHour_one')}
        sorted={column.getIsSorted()}
      />
    ),
    cell: (props) => {
      const row = props.row.original;
      return (
        renderRegistry.get(row._type)?.arbeitszeit(props.row.original) ?? (
          <>Nothing</>
        )
      );
    },
    sortingFn: sortArbeitszeit,
  },
  {
    accessorKey: 'Dauer',
    header: ({ column }) => (
      <SortableHeader
        title={t('entities.duration')}
        sorted={column.getIsSorted()}
      />
    ),
    cell: (props) => {
      const row = props.row.original;
      return (
        renderRegistry.get(row._type)?.dauer(props.row.original) ?? <>Nothing</>
      );
    },
    sortingFn: sortDauer,
  },
  {
    id: 'notes',
    cell: (props) => {
      const row = props.row.original;
      return (
        renderRegistry.get(row._type)?.hinweise(props.row.original) ?? (
          <>Nothing</>
        )
      );
    },
  },
  {
    header: '',
    id: 'actions',
    cell: (props) => {
      const row = props.row.original;
      return (
        renderRegistry.get(row._type)?.action(props.row.original) ?? (
          <>Nothing</>
        )
      );
    },
  },
];

export function ToDoTableContent({
  mitarbeiterFilter = '',
  arbeitsbereichFilter,
  abteilungFilter,
  data,
}: {
  mitarbeiterFilter?: string;
  arbeitsbereichFilter: Arbeitsbereich[];
  abteilungFilter: Abteilung[];
  data: ToDoRowModel[];
}) {
  const { currentStandort } = useStandortContext();

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([
    {
      id: 'Mitarbeiter',
      value: `${mitarbeiterFilter}`,
    },
  ]);
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'Arbeitszeit', desc: false },
  ]);

  useEffect(() => {
    setColumnFilters([
      {
        id: 'Mitarbeiter',
        value: `${mitarbeiterFilter}`,
      },
      {
        id: 'Arbeitsbereich',
        value: {
          arbeitsbereiche: arbeitsbereichFilter.map(
            (arbeitsbereich) => arbeitsbereich.id,
          ),
          abteilungen: currentStandort.abteilungenEnabled
            ? abteilungFilter.map((abteilung) => abteilung.id)
            : [],
        },
      },
    ]);
  }, [
    mitarbeiterFilter,
    arbeitsbereichFilter,
    abteilungFilter,
    currentStandort.abteilungenEnabled,
  ]);

  const table = useReactTable({
    data,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      columnFilters,
      sorting,
    },
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    initialState: {
      columnFilters,
      sorting,
    },
  });

  return (
    <E2Ntable className="w-full border-separate">
      <E2NtableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                onClick={header.column.getToggleSortingHandler()}
                className={`text-xs normal-case font-semibold h-0 pt-4 px-0`}>
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </E2NtableHeader>
      <TableBody>
        {table.getSortedRowModel().rows?.length ? (
          table.getSortedRowModel().rows.map((row) => {
            return (
              <E2NtableRow key={row.id} className="group hover:bg-transparent">
                {row.getVisibleCells().map((cell, index) => {
                  const isFirst = index === 0;
                  const isLast = index === row.getVisibleCells().length - 1;

                  return (
                    <TableCell
                      key={cell.id}
                      className={`
                      bg-white p-0 border-primary-red-400 group-hover:border-gray-300
                      ${index === 0 ? 'max-w-72 w-72 overflow-hidden' : ''}
                      ${index === 1 ? 'max-w-64 w-64  overflow-hidden' : ''}
                      ${index === 2 ? 'overflow-hidden' : ''}
                      ${index === 3 ? 'max-w-52 w-52 overflow-hidden' : ''}
                      ${index === 4 ? 'max-w-64 w-64 overflow-hidden' : ''}
                      ${index === 5 ? 'max-w-24 w-24 overflow-hidden' : ''}

                      ${row.original instanceof Konflikt ? 'border-y-2' : ''}
                      ${isFirst ? 'rounded-l-lg' : ''}
                      ${isLast ? 'rounded-r-lg ' : ''}
                      ${
                        isLast && row.original instanceof Konflikt
                          ? 'border-r-2'
                          : ''
                      }
                      ${
                        isFirst && row.original instanceof Konflikt
                          ? 'border-l-2'
                          : ''
                      }
                      ${
                        cell.column.id === 'email' ? 'hidden md:table-cell' : ''
                      }
                    `}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  );
                })}
              </E2NtableRow>
            );
          })
        ) : (
          <TableRow>
            <TableCell colSpan={columns.length} className="h-24 text-center">
              <NoDataFallback />
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </E2Ntable>
  );
}
